import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, STATUS_LABELS, STATUS_CHIP } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { ScanLine, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "../lib/auth";
import BarcodeCameraScanner from "../components/BarcodeCameraScanner";

// which statuses each role can set via manual button
const ROLE_TRANSITIONS = {
  admin: ["URETIMDE", "HAZIR", "URETIM_BEKLIYOR"],
  production_manager: ["URETIMDE", "HAZIR", "URETIM_BEKLIYOR"],
  production_operator: ["URETIMDE", "HAZIR", "URETIM_BEKLIYOR"],
  shipping_operator: ["HAZIR"],
  view_only: [],
};

const BTN_LABELS = {
  URETIMDE: "Üretime Al",
  HAZIR: "Hazır",
  SEVKIYATA_HAZIR: "Hazır",
  URETIM_BEKLIYOR: "Beklemeye Al",
};

export default function ScanPage() {
  const [barcode, setBarcode] = useState("");
  const [piece, setPiece] = useState(null);
  const [flash, setFlash] = useState(""); // 'ok' | 'err' | ''
  const [lastMsg, setLastMsg] = useState("");
  const inputRef = useRef(null);
  const { user } = useAuth();
  const allowed = ROLE_TRANSITIONS[user?.role] || [];

  useEffect(() => { inputRef.current?.focus(); }, []);

  const lookup = async (bc) => {
    if (!bc) return;
    try {
      const { data } = await api.get(`/pieces/by-barcode/${encodeURIComponent(bc)}`);
      setPiece(data);
      setFlash("ok");
      setLastMsg("Barkod okundu");
      setTimeout(() => setFlash(""), 700);
    } catch (e) {
      setPiece(null);
      setFlash("err");
      setLastMsg(formatApiError(e));
      toast.error(formatApiError(e));
      setTimeout(() => setFlash(""), 700);
    } finally {
      setBarcode("");
      inputRef.current?.focus();
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); lookup(barcode.trim()); }
  };

  const doStatus = async (newStatus) => {
    if (!piece) return;
    try {
      const { data } = await api.post("/pieces/status", { barcode: piece.barcode, new_status: newStatus });
      toast.success(`${STATUS_LABELS[data.prev_status]} → ${STATUS_LABELS[data.new_status]}`);
      // Refresh piece
      const { data: fresh } = await api.get(`/pieces/by-barcode/${encodeURIComponent(piece.barcode)}`);
      setPiece(fresh);
      setFlash("ok");
      setTimeout(() => setFlash(""), 700);
    } catch (e) {
      toast.error(formatApiError(e));
      setFlash("err");
      setTimeout(() => setFlash(""), 700);
    } finally {
      inputRef.current?.focus();
    }
  };

  const flashClass = flash === "ok" ? "flash-success" : flash === "err" ? "flash-error" : "";

  return (
    <div className={flashClass}>
      <PageHeader title="Barkod Tarama" subtitle="Barkod tarayıcı ile hızlı durum güncelleme" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scanner input */}
        <section className="card-industrial p-6">
          <label className="block text-xs uppercase tracking-widest font-bold text-slate-600 mb-2">Barkodu okutun veya elle girin</label>
          <div className="flex items-center gap-3">
            <ScanLine className="text-blue-700 flex-shrink-0" size={44} strokeWidth={2.5} />
            <input
              ref={inputRef}
              data-testid="scan-input"
              autoFocus
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Barkod..."
              className="scan-input flex-1"
            />
            <button data-testid="scan-lookup-btn" onClick={() => lookup(barcode.trim())} className="px-6 py-6 bg-blue-700 hover:bg-blue-800 text-white font-bold text-lg uppercase">
              Ara
            </button>
          </div>
          {lastMsg && (
            <div className={`mt-4 p-3 border-2 flex items-center gap-2 font-semibold ${flash === "err" ? "bg-red-50 border-red-500 text-red-800" : "bg-emerald-50 border-emerald-500 text-emerald-800"}`}>
              {flash === "err" ? <XCircle size={20} /> : <CheckCircle2 size={20} />}
              <span data-testid="scan-message">{lastMsg}</span>
            </div>
          )}
          <BarcodeCameraScanner onDetected={(code) => lookup(code.trim())} className="mt-4" />
        </section>

        {/* Piece detail */}
        <section className="card-industrial p-6">
          {!piece ? (
            <div className="h-full grid place-items-center text-slate-400">
              <div className="text-center">
                <ScanLine size={64} className="mx-auto mb-3 opacity-40" />
                <div className="font-semibold">Bir barkod okutun</div>
              </div>
            </div>
          ) : (
            <div data-testid="scan-piece-detail">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Barkod</div>
                  <div className="font-mono tabular-nums text-2xl font-bold text-slate-900">{piece.barcode}</div>
                </div>
                <span className={STATUS_CHIP[piece.status] + " text-sm"}>{STATUS_LABELS[piece.status]}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Müşteri" value={piece.customer_name} />
                <Field label="Sipariş No" value={piece.order_number} mono />
                <Field label="Ürün" value={`${piece.category} — ${piece.model}`} />
                <Field label="Ölçü" value={piece.size} mono />
                <Field label="Ürün" value={`${piece.product_number} / ${piece.product_total}`} mono />
                <Field label="Parça" value={`${piece.piece_number} / ${piece.piece_total}`} mono />
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Action buttons */}
      {piece && allowed.length > 0 && (
        <section className="mt-6">
          <h2 className="font-display font-bold uppercase tracking-wide text-sm text-slate-500 mb-3">Durum Güncelle</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {allowed.map((s) => (
              <button
                key={s}
                data-testid={`status-btn-${s}`}
                onClick={() => doStatus(s)}
                disabled={s === piece.status}
                className={`h-24 border-2 rounded-none font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 text-lg ${
                  s === piece.status
                    ? "bg-slate-100 border-slate-300 text-slate-400 cursor-not-allowed"
                    : "bg-white border-slate-800 hover:bg-slate-900 hover:text-white"
                }`}
              >
                <ArrowRight size={22} /> {BTN_LABELS[s] || STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className={`${mono ? "font-mono tabular-nums" : ""} text-slate-900 font-bold text-lg`}>{value}</div>
    </div>
  );
}
